import { useCallback, useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { ContractualRentMetrics } from "./contractualRent.types";

export const useContractualRent = () => {
  const { session } = useSession();
  const [metrics, setMetrics] = useState<ContractualRentMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      if (!session) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/metrics/contractual-rent`,
          { method: "GET" },
          session,
        );
        if (!response.ok) {
          throw new Error(
            `Contractuele huur ophalen mislukt (HTTP ${response.status})`,
          );
        }
        const data: ContractualRentMetrics = await response.json();
        if (!isCancelled()) setMetrics(data);
      } catch (err) {
        Sentry.captureException(err, {
          tags: { feature: "contractual-rent", component: "dashboard" },
        });
        if (!isCancelled()) {
          setError("De contractuele huur kon niet geladen worden.");
        }
      } finally {
        if (!isCancelled()) setIsLoading(false);
      }
    },
    [session],
  );

  useEffect(() => {
    let cancelled = false;
    load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  return { metrics, isLoading, error, reload: () => load() };
};
